import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faListCheck } from '@fortawesome/free-solid-svg-icons';
import { guestApi } from '../../utils/portalApi';

/**
 * The guest's tasks (guest role, Phase 3): what is open, and what they have
 * finished - completed work stays listed, as it does for an employee.
 */

export const dueLabel = (task) => {
    const at = task.overdueAt || task.dueDate;
    if (!at) return '';
    const d = new Date(at);
    const text = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
        + (task.dueTime ? ` ${task.dueTime}` : '');
    return task.status !== 'Completed' && d.getTime() < Date.now() ? `Overdue · was due ${text}` : `Due ${text}`;
};

export const statusTone = (status) => ({
    Pending: 'pending', 'In Progress': 'progress', 'On Hold': 'hold', Completed: 'done'
}[status] || 'pending');

const GuestTasks = ({ onAuthError, onCounts }) => {
    const navigate = useNavigate();
    const [view, setView] = useState('open');
    const [data, setData] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        let cancelled = false;
        setData(null);
        setError('');
        guestApi.get('/tasks', { params: { view } })
            .then(({ data: body }) => {
                if (cancelled) return;
                setData(body);
                onCounts?.(body.counts);
            })
            .catch((err) => {
                if (cancelled) return;
                onAuthError(err);
                setError(err.response?.data?.message || 'Could not load your tasks.');
                setData({ items: [], counts: { open: 0, completed: 0 } });
            });
        return () => { cancelled = true; };
    }, [view, onAuthError, onCounts]);

    return (
        <main className="guest-page">
            <h1><FontAwesomeIcon icon={faListCheck} /> Tasks</h1>
            <div className="guest-tabs" role="tablist">
                <button type="button" role="tab" aria-selected={view === 'open'} onClick={() => setView('open')}>
                    Open{data ? ` (${data.counts.open})` : ''}
                </button>
                <button type="button" role="tab" aria-selected={view === 'completed'} onClick={() => setView('completed')}>
                    Completed{data ? ` (${data.counts.completed})` : ''}
                </button>
            </div>

            {!data && <div className="portal-spinner" />}
            {error && <p className="portal-error">{error}</p>}
            {data && !data.items.length && !error && (
                <p className="guest-muted">
                    {view === 'open' ? 'Nothing open right now. Tasks the team gives you will appear here.' : 'No completed tasks yet.'}
                </p>
            )}

            {data && data.items.length > 0 && (
                <ul className="guest-meetings">
                    {data.items.map((t) => (
                        <li key={t._id}>
                            <button type="button" className="guest-meeting guest-task-row" onClick={() => navigate(`/guest/tasks/${t._id}`)}>
                                <span className="guest-meeting-body">
                                    <strong>{t.title}</strong>
                                    <span>{t.projectName || 'Office task'}{t.assignedBy?.name ? ` · from ${t.assignedBy.name}` : ''}</span>
                                    <span>
                                        {t.status === 'Completed' && t.completedAt
                                            ? `Completed ${new Date(t.completedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`
                                            : dueLabel(t)}
                                    </span>
                                </span>
                                <span className="guest-task-tags">
                                    <span className={`guest-status ${statusTone(t.status)}`}>{t.status}</span>
                                    <span className="guest-priority">{t.priority}</span>
                                </span>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </main>
    );
};

export default GuestTasks;
